<template>
  <div class="user-status-filter-item indoor">
    <label
      class="item-label"
      v-t.preserve="'appUserStatusFilterIndoor.indoorSiteBuilding'"
    />
    <indoor-site-and-building-selector
      ref="selector"
      id-prefix="user-status-filter-indoor"
      v-bind:indoor-site-ids="localValue.indoorSiteIds"
      v-bind:indoor-building-ids="localValue.indoorBuildingIds"
      v-on:selected="$_onSiteBuildingSelected"
    />
    <template v-if="isIndoorLevelSelectable">
      <label
        class="item-label"
        v-t.preserve="'appUserStatusFilterIndoor.indoorLevel'"
      />
      <b-form-select
        size="sm"
        v-model="localValue.indoorLevelId"
        v-bind:options="indoorLevelOptions"
        v-on:input="$_onChange"
      />
    </template>
  </div>
</template>

<script>
import IndoorSiteAndBuildingSelector from '@/components/indoor/AppIndoorSiteAndBuildingSelector.vue'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { mapGetters } from 'vuex'
import _ from 'lodash'

export default {
  name: 'AppUserStatusFilterIndoor',
  components: {
    IndoorSiteAndBuildingSelector,
  },
  props: {
    /**
     * サイトID
     */
    indoorSiteIds: {
      type: Array,
      default: null,
    },
    /**
     * ビルID
     */
    indoorBuildingIds: {
      type: Array,
      default: null,
    },
    /**
     * フロアID
     */
    indoorLevelId: {
      type: Number,
      default: null,
    },
  },
  emits: [
    /**
     * 選択値変更イベント
     * @param {Object} value 選択値
     * @param {String} text 選択値表示テキスト
     */
    'changed',
  ],
  setup() {
    const { handleApiError } = useHandleApiError()
    return { handleApiError }
  },
  data: function () {
    return {
      // ローカル値
      localValue: {
        indoorSiteIds: null,
        indoorBuildingIds: null,
        indoorLevelId: null,
      },
      // サイト・ビルの選択値テキスト
      indoorSiteBuildingText: '',
    }
  },
  computed: {
    ...mapGetters('indoorSite', ['indoorSiteMap']),
    ...mapGetters('indoorBuilding', ['indoorBuildingMap']),
    /**
     * 反映用にまとめたprops値
     * @type {Object}
     */
    valueToApply: function () {
      return {
        indoorSiteIds: this.indoorSiteIds,
        indoorBuildingIds: this.indoorBuildingIds,
        indoorLevelId: this.indoorLevelId,
      }
    },
    /**
     * 反映用値(watcher監視用)
     * @type {string}
     */
    valueToApplyForWatch: function () {
      return JSON.stringify(this.valueToApply)
    },
    /**
     * フロアを選択可能な場合はtrue
     * @type {Boolean}
     */
    isIndoorLevelSelectable: function () {
      if (this.localValue.indoorBuildingIds?.length !== 1) return false
      const building = this.indoorBuildingMap.get(
        this.localValue.indoorBuildingIds[0]
      )
      return building && building.indoor_levels.length
    },
    /**
     * フロアセレクターの選択肢
     * @type {Array.<Object>}
     */
    indoorLevelOptions: function () {
      if (!this.isIndoorLevelSelectable) return []

      const building = this.indoorBuildingMap.get(
        this.localValue.indoorBuildingIds[0]
      )
      return building.indoor_levels.map((v) => ({
        value: v.id,
        text: v.name,
      }))
    },
    /**
     * イベント返却用のテキスト
     * @type {String}
     */
    resultText: function () {
      const { indoorLevelId, indoorBuildingIds } = this.localValue

      let levelName = ''
      if (indoorLevelId && indoorBuildingIds?.length) {
        const building = this.indoorBuildingMap.get(indoorBuildingIds[0])
        const level = building.indoor_levels.find((l) => l.id === indoorLevelId)
        if (level) {
          levelName = level.short_name || level.name
        }
      }

      if (this.isIndoorLevelSelectable && indoorLevelId) {
        return `${this.indoorSiteBuildingText}(${levelName})`
      } else {
        return this.indoorSiteBuildingText
      }
    },
    /**
     * イベント返却値
     * @type {Object}
     */
    result: function () {
      return { value: this.localValue, text: this.resultText }
    },
  },
  watch: {
    valueToApplyForWatch: function () {
      this.$_applyToValue()
    },
    isIndoorLevelSelectable: function () {
      this.$_onIndoorLevelSelectableChanged()
    },
  },
  created: function () {
    this.fetchPromises = [
      this.$_updateIndoorSites(),
      this.$_updateIndoorBuildings(),
    ]
  },
  mounted: async function () {
    // 初回表示時だけテキストをすぐ反映したいので
    // $_applyToValue 経由で値を反映することで1回だけ change イベントを listen して
    // テキストを取得後にイベントを発行する
    await this.$nextTick()
    // 選択肢が出来上がってからでないとテキストが取得できないので API リクエストも待つ
    await Promise.all(this.fetchPromises)
    this.$_applyToValue()
  },
  methods: {
    /**
     * ビルデータ更新
     */
    $_updateIndoorBuildings: async function () {
      try {
        await this.$store.dispatch(
          'indoorBuilding/updateIndoorBuildingsWithThrottle'
        )
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.info(
            'failed to fetch indoor buildings. use fetched before.'
          )
        })
      }
    },

    /**
     * サイトデータ更新
     */
    $_updateIndoorSites: async function () {
      try {
        await this.$store.dispatch('indoorSite/updateIndoorSitesWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.info('failed to fetch indoor sites. use fetched before.')
        })
      }
    },

    /**
     * props 値を反映する
     */
    $_applyToValue: function () {
      if (_.isEqual(this.localValue, this.valueToApply)) return

      const isSiteSame = _.isEqual(
        this.localValue.indoorSiteIds,
        this.valueToApply.indoorSiteIds
      )
      const isBuildingSame = _.isEqual(
        this.localValue.indoorBuildingIds,
        this.valueToApply.indoorBuildingIds
      )
      if (!isSiteSame || !isBuildingSame) {
        // 反映後の change イベントでユーザーアクションで閉じたときの処理を実行させて表示テキストを親に emit する
        this.$refs['selector'].$once('change', this.$_onSiteBuildingSelected)
      }
      this.localValue = { ...this.valueToApply }
    },

    /**
     * サイト・ビル選択変更時処理
     * @param {Object} value 選択値
     * @param {String} text 選択値テキスト
     */
    $_onSiteBuildingSelected: function (value, text) {
      if (value) {
        const { indoorSiteId, indoorBuildingId } = value
        this.localValue.indoorSiteIds = [...indoorSiteId].sort((a, b) => a - b)
        this.localValue.indoorBuildingIds = [...indoorBuildingId].sort(
          (a, b) => a - b
        )
        this.indoorSiteBuildingText = text
      } else {
        this.localValue.indoorSiteIds = null
        this.localValue.indoorBuildingIds = null
        this.indoorSiteBuildingText = ''
      }
      this.$_onChange()
    },

    /**
     * フロアの選択可否状態変更時の処理
     */
    $_onIndoorLevelSelectableChanged: function () {
      if (!this.isIndoorLevelSelectable) {
        // フロアを選択できなくなったらフロアIDの値をクリア
        this.localValue.indoorLevelId = null
        this.$_onChange()
        return
      }

      // フロアが選択されていない時はデフォルトフロアを選択する
      if (!this.localValue.indoorLevelId) {
        const indoorBuilding = this.indoorBuildingMap.get(
          this.localValue.indoorBuildingIds[0]
        )
        const indoorLevel = indoorBuilding.indoor_levels.find(
          (level) => level.default_level
        )
        this.localValue.indoorLevelId = indoorLevel.id
        this.$_onChange()
      }
    },

    /**
     * 変更時処理
     */
    $_onChange: function () {
      this.$emit('changed', this.result)
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../assets/scss/parts/user/user-status-filter-item';
</style>
